import { useEffect, useRef, useState } from "react";
import { Button, Card } from "@clevis/shared-ui";
import { call } from "../../lib/callFunction";
import { useAuth } from "../../hooks/useAuth";

// Signed in but no membership yet. For provider sign-ins we immediately check
// whether an invite matches the account's email; failing that, this explains
// the invite-only model. First-ever run offers the platform bootstrap.
export function Welcome() {
  const { user, refreshClaims, signOut } = useAuth();
  const [busy, setBusy] = useState(true);
  const [message, setMessage] = useState("Checking for an invite…");
  const checked = useRef(false);

  useEffect(() => {
    if (checked.current) return;
    checked.current = true;
    void (async () => {
      try {
        const res = await call<{ found?: boolean; alreadyMember?: boolean }>(
          "claimMyInvite",
          {},
          { mutation: false },
        );
        if (res.alreadyMember || res.found) {
          await refreshClaims();
          window.location.href = "/";
          return;
        }
        setMessage("");
      } catch (err) {
        setMessage((err as Error).message);
      } finally {
        setBusy(false);
      }
    })();
  }, [refreshClaims]);

  async function bootstrap() {
    setBusy(true);
    setMessage("");
    try {
      const res = await call<{ note: string }>("bootstrapPlatform", {}, { mutation: false });
      await refreshClaims();
      setMessage(res.note);
      window.location.href = "/";
    } catch (err) {
      setMessage((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-full items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <h1 className="mb-1 text-lg font-bold text-ink">Almost there</h1>
        <p className="mb-4 text-sm text-steel">
          Signed in as {user?.email}, but no company invite matches this email yet. Ask your company
          admin to invite you, then sign in again.
        </p>
        {busy ? (
          <div className="mx-auto h-6 w-6 animate-spin rounded-full border-2 border-steel/40 border-t-brandorange" />
        ) : null}
        {message ? <p className="text-xs text-ink">{message}</p> : null}
        <div className="mt-6 flex items-center justify-between border-t border-steel/20 pt-3">
          <button className="text-xs text-steel hover:text-ink" onClick={bootstrap}>
            First run? Bootstrap the platform
          </button>
          <Button variant="ghost" className="px-3 py-1 text-xs" onClick={() => void signOut()}>
            Sign out
          </Button>
        </div>
      </Card>
    </div>
  );
}
